import type {
  Meeting,
  MeetingInput,
  MeetingPreferences,
  MeetingPreferencesInput,
  MeetingSlot,
  SchedulingPage,
  SchedulingPageInput,
} from "@sales-ops-amplify/shared";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "./client";

export type MeetingView = "upcoming" | "past" | "cancelled" | "all";

export function listMeetings(params?: {
  view?: MeetingView;
  q?: string;
  leadId?: string;
  opportunityId?: string;
}): Promise<{ meetings: Meeting[] }> {
  return apiGet(
    "/api/meetings",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function createMeeting(
  input: MeetingInput,
): Promise<{ meeting: Meeting }> {
  return apiPost("/api/meetings", input);
}

export function getMeeting(meetingId: string): Promise<{ meeting: Meeting }> {
  return apiGet(`/api/meetings/${meetingId}`);
}

export function updateMeeting(
  meetingId: string,
  patch: Partial<MeetingInput> & {
    status?: Meeting["status"];
    outcomeNotes?: string;
  },
): Promise<{ meeting: Meeting }> {
  return apiPatch(`/api/meetings/${meetingId}`, patch);
}

export function cancelMeeting(
  meetingId: string,
  outcomeNotes?: string,
): Promise<{ meeting: Meeting }> {
  return apiPost(`/api/meetings/${meetingId}/cancel`, { outcomeNotes });
}

export function completeMeeting(
  meetingId: string,
  outcomeNotes?: string,
): Promise<{ meeting: Meeting }> {
  return apiPost(`/api/meetings/${meetingId}/complete`, { outcomeNotes });
}

export function noShowMeeting(
  meetingId: string,
  outcomeNotes?: string,
): Promise<{ meeting: Meeting }> {
  return apiPost(`/api/meetings/${meetingId}/no-show`, { outcomeNotes });
}

export function syncMeetings(): Promise<{
  imported: number;
  meetings: Meeting[];
}> {
  return apiPost("/api/meetings/sync", {});
}

export function getMeetingPreferences(): Promise<{
  preferences: MeetingPreferences;
  calendar: { connected: boolean; provider?: "google" | "outlook" };
}> {
  return apiGet("/api/meetings/preferences");
}

export function updateMeetingPreferences(
  patch: MeetingPreferencesInput,
): Promise<{ preferences: MeetingPreferences }> {
  return apiPut("/api/meetings/preferences", patch);
}

export function listSchedulingPages(): Promise<{ pages: SchedulingPage[] }> {
  return apiGet("/api/meetings/scheduling-pages");
}

export function createSchedulingPage(
  input: SchedulingPageInput,
): Promise<{ page: SchedulingPage }> {
  return apiPost("/api/meetings/scheduling-pages", input);
}

export function updateSchedulingPage(
  pageId: string,
  patch: Partial<SchedulingPageInput>,
): Promise<{ page: SchedulingPage }> {
  return apiPatch(`/api/meetings/scheduling-pages/${pageId}`, patch);
}

export function deleteSchedulingPage(pageId: string): Promise<{ ok: boolean }> {
  return apiDelete(`/api/meetings/scheduling-pages/${pageId}`);
}

export function listAvailabilitySlots(params: {
  pageId?: string;
  from: string;
  to: string;
  timezone?: string;
}): Promise<{ slots: MeetingSlot[] }> {
  return apiGet(
    "/api/meetings/availability/slots",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export type PublicBookingMeta = {
  slug: string;
  brandingTitle: string;
  description?: string;
  durationMin: number;
  timezone: string;
  questions: Array<{ id: string; label: string; required?: boolean }>;
  locationType: string;
  slots: MeetingSlot[];
};

export async function getPublicBooking(
  slug: string,
  params?: { from?: string; to?: string; timezone?: string },
): Promise<PublicBookingMeta> {
  const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
  let url = `${API_BASE}/api/public/book/${encodeURIComponent(slug)}`;
  if (params) {
    const qs = new URLSearchParams();
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.timezone) qs.set("timezone", params.timezone);
    const s = qs.toString();
    if (s) url += `?${s}`;
  }
  const res = await fetch(url);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      typeof body === "object" && body && "error" in body
        ? String((body as { error: string }).error)
        : "Booking page not found",
    );
  }
  return res.json();
}

export async function bookPublicSlot(
  slug: string,
  body: {
    startAt: string;
    endAt?: string;
    guestName: string;
    guestEmail: string;
    notes?: string;
    answers?: Record<string, string>;
  },
): Promise<{
  meeting: {
    meetingId: string;
    title: string;
    startAt: string;
    endAt: string;
    timezone: string;
    conferencingUrl?: string;
  };
}> {
  const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
  const res = await fetch(
    `${API_BASE}/api/public/book/${encodeURIComponent(slug)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      typeof err === "object" && err && "error" in err
        ? String((err as { error: string }).error)
        : "Booking failed",
    );
  }
  return res.json();
}
